"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { FastOrderService } from "@/services/FastOrderService";
import type { FastOrderOutput } from "@/interfaces/fastOrders";
import {
  Plus,
  Trash,
  Edit,
  Eye,
  LayoutGrid,
  List,
  CheckCircle2,
  Clock,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDeleteConfirmation } from "@/hooks/use-delete-confirmation";
import { DeleteConfirmationAlert } from "@/components/DeleteConfirmationAlert";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { FastOrderTotals } from "./components/FastOrderTotals";
import { Progress } from "@/components/ui/progress";
import { motion } from "framer-motion";
import { numberToMoney } from "@/util/moneyConversion";
import type { PaginatedResponse, Pagination } from "@/interfaces";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PaginationControls = ({
  pagination,
  onPageChange,
  onPageSizeChange,
}: {
  pagination: Pagination;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}) => {
  if (!pagination) {
    return null; // Retorna null se pagination for undefined
  }

  const { page, pageSize, total, pages } = pagination;
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-2">
      <div className="text-sm text-gray-500">
        Mostrando {total > 0 ? startItem : 0} a {endItem} de {total} pedidos
        rápidos
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center mr-4">
          <span className="text-sm text-gray-500 mr-2">Itens por página:</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={pageSize.toString()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(pages, 5) }, (_, i) => {
              let pageNumber: number;

              // Logic to show pages around current page
              if (pages <= 5) {
                pageNumber = i + 1;
              } else if (page <= 3) {
                pageNumber = i + 1;
              } else if (page >= pages - 2) {
                pageNumber = pages - 4 + i;
              } else {
                pageNumber = page - 2 + i;
              }

              return (
                <Button
                  key={pageNumber}
                  variant={page === pageNumber ? "default" : "outline"}
                  size="icon"
                  className={cn(
                    "h-8 w-8",
                    page === pageNumber && "bg-[#EE7B11] hover:bg-[#d06a0a]"
                  )}
                  onClick={() => onPageChange(pageNumber)}
                >
                  {pageNumber}
                </Button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

type ViewMode = "grid" | "list";

const FastOrders = () => {
  const service = new FastOrderService();
  const navigate = useNavigate();
  // Remover searchParams

  const [fastOrders, setFastOrders] = useState<FastOrderOutput[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: 10,
    total: 0,
    pages: 0,
    links: {
      self: "",
    },
  });

  // Refs para controle de estado e evitar loops
  const isFetchingRef = useRef(false);

  // Order status statistics
  const [orderStats, setOrderStats] = useState({
    completed: { count: 0, percentage: 0 },
    inProgress: { count: 0, percentage: 0 },
  });

  // Usando o hook para gerenciar o estado de exclusão
  const {
    isOpen,
    itemToDelete,
    open: openDeleteDialog,
    close: closeDeleteDialog,
  } = useDeleteConfirmation<string>();

  // Função para buscar pedidos com parâmetros explícitos
  const fetchOrders = useCallback(
    async (page = pagination.page, pageSize = pagination.pageSize) => {
      // Evitar chamadas duplicadas
      if (isFetchingRef.current) return;

      try {
        isFetchingRef.current = true;
        setIsLoading(true);
        const queryParams: Record<string, string | number> = {
          page,
          pageSize,
        };

        const response: PaginatedResponse<FastOrderOutput> =
          await service.findAll(queryParams);

        // Usar o novo formato de resposta paginada
        setFastOrders(response.data);

        // Verificar se a página atual é válida
        if (response.pagination.pages > 0 && page > response.pagination.pages) {
          // Se a página atual for maior que o número total de páginas, redirecionar para a última página
          const lastPage = response.pagination.pages;

          // Atualizar paginação sem disparar efeitos colaterais
          setPagination((prev) => ({
            ...prev,
            page: lastPage,
          }));
        } else {
          // Atualizar paginação normalmente
          setPagination({
            ...response.pagination,
            page,
            pageSize,
          });
        }

        // Calculate order statistics
        calculateOrderStats(response.data);
      } catch (error) {
        console.error("Erro ao buscar pedidos rápidos:", error);

        if (page !== 1) {
          setPagination((prev) => ({
            ...prev,
            page: 1,
          }));
        }
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    },
    [pagination.page, pagination.pageSize]
  );

  // Inicialização - executada apenas uma vez
  useEffect(() => {
    fetchOrders();
  }, []);

  // Efeito para atualizar quando a paginação mudar
  useEffect(() => {
    fetchOrders(pagination.page, pagination.pageSize);
  }, [pagination.page, pagination.pageSize, fetchOrders]);

  // Modificar a função calculateOrderStats para usar a lógica correta de pagamentos
  const calculateOrderStats = (orders: FastOrderOutput[]) => {
    if (!orders.length) return;

    const total = orders.length;

    // Count orders by status baseado nos pagamentos reais
    const completed = orders.filter((order) => {
      if (!order.payments || !Array.isArray(order.payments)) return false;
      const paidAmount = order.payments.reduce(
        (acc, curr) => acc + (curr.amount || 0),
        0
      );
      return paidAmount >= (order.totalAmount || 0);
    }).length;

    const inProgress = total - completed;

    setOrderStats({
      completed: {
        count: completed,
        percentage: Math.round((completed / total) * 100),
      },
      inProgress: {
        count: inProgress,
        percentage: Math.round((inProgress / total) * 100),
      },
    });
  };

  const handleNavigateForm = (route?: string) => {
    const targetRoute = route
      ? `/pedidos-rapidos/${route}`
      : "/pedidos-rapidos/novo";
    navigate(targetRoute);
  };

  const handleDelete = (id: string) => {
    openDeleteDialog(id); // Abre o dialog de confirmação
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      try {
        setIsLoading(true);
        await service.delete(itemToDelete);
        await fetchOrders(pagination.page, pagination.pageSize);
      } catch (error) {
        console.error("Erro ao excluir pedido rápido:", error);
      } finally {
        setIsLoading(false);
      }
    }
    closeDeleteDialog(); // Fecha o dialog após a exclusão
  };

  // Substituir em pendingAmount
  const pendingAmount = (order: FastOrderOutput) => {
    if (!order.payments || !Array.isArray(order.payments))
      return order.totalAmount || 0;
    const paidAmount = order.payments.reduce(
      (acc, curr) => acc + (curr.amount || 0),
      0
    );
    return (order.totalAmount || 0) - paidAmount;
  };

  // Adicionar as funções de manipulação de paginação no componente FastOrders
  const handlePageChange = (newPage: number) => {
    if (newPage === pagination.page) return;
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (newPageSize === pagination.pageSize) return;
    setPagination((prev) => ({
      ...prev,
      pageSize: newPageSize,
      page: 1, // Reset to first page when changing page size
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Pedidos Rápidos</h1>
        <Button
          onClick={() => handleNavigateForm()}
          size="sm"
          className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white w-full md:w-auto"
        >
          <Plus className="h-4 w-4 mr-2" /> Cadastrar Pedido Rápido
        </Button>
      </div>

      {/* Order Statistics */}
      <FastOrderTotals orders={fastOrders} />

      {/* Order Status Card - Removida a coluna de cancelados */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-6"
      >
        <Card className="border-[#EE7B11]/20 shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-xl flex items-center">
              <CheckCircle2 className="mr-2 h-5 w-5 text-[#EE7B11]" />
              Status dos Pedidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                    <p className="text-sm font-medium">Concluídos</p>
                  </div>
                  <p className="text-sm font-medium text-green-600">
                    {orderStats.completed.percentage}%
                  </p>
                </div>
                <Progress
                  value={orderStats.completed.percentage}
                  className="h-2 bg-gray-100"
                />
                <p className="text-xs text-gray-500 text-right">
                  {orderStats.completed.count} pedidos
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-[#EE7B11]" />
                    <p className="text-sm font-medium">Em andamento</p>
                  </div>
                  <p className="text-sm font-medium text-[#EE7B11]">
                    {orderStats.inProgress.percentage}%
                  </p>
                </div>
                <Progress
                  value={orderStats.inProgress.percentage}
                  className="h-2 bg-gray-100"
                />
                <p className="text-xs text-gray-500 text-right">
                  {orderStats.inProgress.count} pedidos
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <div className="flex justify-end gap-2 mb-6">
        {/* View mode toggle */}
        <ToggleGroup
          type="single"
          value={viewMode}
          onValueChange={(value) => value && setViewMode(value as ViewMode)}
        >
          <ToggleGroupItem
            value="list"
            aria-label="Visualização em lista"
            className="h-11 px-3"
          >
            <List className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Lista</span>
          </ToggleGroupItem>
          <ToggleGroupItem
            value="grid"
            aria-label="Visualização em cards"
            className="h-11 px-3"
          >
            <LayoutGrid className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Cards</span>
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* Content area */}
      <div className="w-full">
        {fastOrders.length === 0 ? (
          <div className="text-center p-12 bg-gray-50 rounded-lg border border-gray-100">
            <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-6 w-6 text-gray-400"
              >
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                <path d="M12 11h4"></path>
                <path d="M12 16h4"></path>
                <path d="M8 11h.01"></path>
                <path d="M8 16h.01"></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              Nenhum pedido rápido encontrado
            </h3>
            <p className="text-gray-500 max-w-md mx-auto mb-4">
              Você ainda não possui pedidos rápidos cadastrados
            </p>
            <Button
              onClick={() => handleNavigateForm()}
              className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Cadastrar Pedido Rápido
            </Button>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {fastOrders.map((order) => {
              const pending = pendingAmount(order);
              const isPaymentComplete = pending <= 0;

              return (
                <Card
                  key={order.id}
                  className="overflow-hidden border-l-4 cursor-pointer hover:shadow-md transition-all duration-200"
                  style={{
                    borderLeftColor: isPaymentComplete ? "#22c55e" : "#f59e0b",
                  }}
                  onClick={() => handleNavigateForm(order.id)}
                >
                  <CardHeader className="p-4 pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-gray-500">Data</p>
                        <p className="font-medium">
                          {format(new Date(order.orderDate), "dd/MM/yyyy")}
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className={`font-normal text-xs ${
                          isPaymentComplete
                            ? "bg-green-100 text-green-700 border-green-200"
                            : "bg-amber-100 text-amber-700 border-amber-200"
                        }`}
                      >
                        {isPaymentComplete ? "Pago" : "Pendente"}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-sm text-gray-500">Valor total</p>
                          <p className="font-medium text-green-700">
                            {numberToMoney(order.totalAmount || 0)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Pendente</p>
                          <p
                            className={`font-medium ${
                              isPaymentComplete
                                ? "text-green-700"
                                : "text-amber-600"
                            }`}
                          >
                            {numberToMoney(pending)}
                          </p>
                        </div>
                      </div>

                      {order.description && (
                        <div className="pt-2">
                          <p className="text-sm text-gray-500">Descrição</p>
                          <p className="text-sm line-clamp-2">
                            {order.description}
                          </p>
                        </div>
                      )}

                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavigateForm(`${order.id}/editar`);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8 border-red-500 text-red-500 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(order.id);
                          }}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden bg-white">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow>
                  <TableHead className="font-semibold">Data</TableHead>
                  <TableHead className="font-semibold">Valor total</TableHead>
                  <TableHead className="font-semibold">
                    Total pendente
                  </TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold text-right">
                    Ações
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fastOrders.map((order) => {
                  const pending = pendingAmount(order);
                  const isPaymentComplete = pending <= 0;

                  return (
                    <TableRow
                      key={order.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleNavigateForm(order.id)}
                    >
                      <TableCell>
                        {format(new Date(order.orderDate), "dd/MM/yyyy")}
                      </TableCell>
                      <TableCell className="font-medium text-green-700">
                        {numberToMoney(order.totalAmount || 0)}
                      </TableCell>
                      <TableCell
                        className={
                          isPaymentComplete
                            ? "text-green-700"
                            : "text-amber-600"
                        }
                      >
                        {numberToMoney(pending)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`font-normal text-xs ${
                            isPaymentComplete
                              ? "bg-green-100 text-green-700 border-green-200"
                              : "bg-amber-100 text-amber-700 border-amber-200"
                          }`}
                        >
                          {isPaymentComplete ? "Pago" : "Pendente"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigateForm(order.id);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Detalhes
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigateForm(`${order.id}/editar`);
                            }}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500 text-red-500 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(order.id);
                            }}
                          >
                            <Trash className="h-4 w-4 mr-1" />
                            Excluir
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Adicionar o componente de paginação após o conteúdo principal */}
      {fastOrders.length > 0 && (
        <PaginationControls
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      {/* Componente de confirmação de exclusão */}
      <DeleteConfirmationAlert
        isOpen={isOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão de Pedido"
        message="Tem certeza que deseja excluir este pedido rápido? Esta ação não pode ser desfeita."
        itemName={`Pedido #${itemToDelete}`} // Exibe o ID do pedido no dialog
      />
    </div>
  );
};

export default FastOrders;
